import React, { useEffect, useState } from 'react';
import { ActivityIndicator, FlatList, Keyboard, Pressable } from "react-native";
import type { AccountProps, FocusPositionProps, ItemOrderProps, ItemProps, MyPlayerProps, PlayerBalanceProps, PlayerDepositLimitProps, PlayerWithdrawLimitProps, WalletSettingsProps } from '../../types';
import { ItemOrderApi, ItemOrderHelpers } from '../api';
import { Button, Text, TextInput, View } from '../../Components/Themed';
import { useColors } from '../../constants/useColors';
import { Icons } from '../../Components';
import ManualACHAdd from './ManualACHAdd';


type WithdrawCardProps = {
    player:MyPlayerProps,
    wallet_settings:WalletSettingsProps,
    player_balance:PlayerBalanceProps,
    account:AccountProps,
    instant?:boolean,
    onAccountUpdate:(account:AccountProps) => void,
    onFocusPosition?:(pos:FocusPositionProps) => void,
    deposit_limit:PlayerDepositLimitProps,
    withdraw_limit:PlayerWithdrawLimitProps,
    onTransact:(draft_order:ItemOrderProps) => void,
    onCancel:() => void
}
const sections = ['header','input','limits']
const WithdrawCard = ({ player, wallet_settings, onTransact, instant, player_balance, account, withdraw_limit, onAccountUpdate, onFocusPosition, deposit_limit , onCancel}:WithdrawCardProps) => {
    const Colors = useColors();
    const [ draft_order, setDraftOrder ] = useState<ItemOrderProps>();
    const [ confirm_ach, setConfirmAch ] = useState(false);
    const [ withdraw_item, setWithdrawItem ] = useState<ItemProps | undefined>(undefined);
    useEffect(() => {
        getItemFromServer()
    },[])

    const getItemFromServer = async() => {
        const item = await ItemOrderApi.getItemByIdentifier(instant?'instant_withdraw':'withdraw');
        setWithdrawItem(item);
        setDraftOrder(ItemOrderHelpers.genWithdrawOrder(item, account))
    }

    
    const { errors, available_for_withdraw } = ItemOrderHelpers.isWithdrawOrderValid(account, withdraw_limit, deposit_limit, player_balance, wallet_settings, draft_order)

    const removeFee = (amount:number, feePct:number) => {
        return amount / (1 + feePct);
    }

    const handleTransact = () => {
        if(!draft_order){ return }
        if(errors.length > 0){ return }
        if(!withdraw_item){ return }

        //OK LETS CHECK THE ACCOUNT TYPE AND IF WE NEED ACCOUNT NUMBER INFORMATION
        if(!instant){
            if(!account.ach_detail?.account_number){
                return setConfirmAch(true);
            }
        }
        //Lets get the item
        let draft_item = draft_order.items[0]
        if(!draft_item){ return }
        let fee = withdraw_item.processing_fee ?? 0
        const final_amount = removeFee(Number(draft_order.amount), fee);
        console.log(final_amount)
        onTransact({ ...draft_order, amount:final_amount, total_amount: draft_order.amount as number, items: [{ ...draft_item, amount: final_amount }] })
    }


    let balance = Math.floor(player_balance.balance * 100) / 100
    let limit_available = withdraw_limit.ach_daily_limit - withdraw_limit.last_days_ach_withdraws

    const renderSections = (data: { item: string, index:number }) => {
        switch(data.item){
            case 'header':
                return (
                    <View nativeID='deposit_rules' style={{ padding:10 }}>
                        <Text size={28} textAlign='center' theme='h1'>Withdraw to your bank</Text>
                        {!instant ?
                        <Text style={{ marginTop:10 }} size={14} textAlign='center' theme='description'>Withdraws can take up to 3 business days</Text>
                        :<></>}
                    </View>
                )
            case 'input':
                if(!draft_order){ return <></> }
                return (
                    <View nativeID='deposit_amount' style={{ justifyContent:'center', alignItems:'center', padding:20 }}>
                        <TextInput 
                            style={{ flexGrow:1, width:200, textAlign:'center', color:Colors.text.success, fontSize:28 }}
                            placeholder='0'
                            onFocusPosition={onFocusPosition}
                            keyboardType='decimal-pad'
                            value={draft_order.amount as string}
                            onChangeText={(text) => setDraftOrder({ ...draft_order, amount:text })}
                        />
                    </View>
                )
            case 'limits':
                const withdraws_available = withdraw_limit.withdraws_per_week - withdraw_limit.week_withdraw_count
                const avail_for_withdraw = withdraws_available == 0 ? 0 : Math.min(available_for_withdraw, limit_available)
                return (
                    <Pressable onPress={() => Keyboard.dismiss()}>
                        <View float nativeID='withdraw_limits' style={{ margin:10 }}>
                            <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10, borderBottomWidth:1, borderColor:Colors.borders.light }}>
                                <View transparent style={{ flex:1 }}>
                                    <Text theme='h1'>Withdraw Amount Available</Text>
                                    <Text style={{ marginTop:3 }} theme='description'>Weekly withdraws remaining</Text>
                                </View>
                                <View transparent style={{ alignItems:'flex-end' }}>
                                    <Text theme='h1' color={avail_for_withdraw>0?Colors.text.success:Colors.text.error}>{avail_for_withdraw.toFixed(2)}</Text>
                                    <Text style={{ marginTop:3 }} theme='description' color={withdraws_available>0?Colors.text.success:Colors.text.error}>{withdraws_available.toFixed()} Remaining</Text>
                                </View>
                            </View>
                            <View transparent type='body'>
                                <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                                    <View transparent style={{ flex:1 }}>
                                        <Text theme='h1'>How is this deteremined?</Text>
                                        <Text theme='description' style={{ marginTop:3 }}>Use the lessor of the following amounts</Text>
                                    </View>
                                </View>
                                <View transparent type='body' style={{ padding:10 }}>
                                    <View float style={{ padding:10 }}>
                                        <View transparent type='row' style={{ padding:4 }}>
                                            <Text theme='description' style={{ flex:1 }}>My Balance</Text>
                                            <Text theme='h2'>{balance.toFixed(2)}</Text>
                                        </View>
                                        <View transparent type='row' style={{ padding:4, borderBottomWidth:1, borderColor:Colors.borders.light }}>
                                            <Text theme='description' style={{ flex:1 }}>Unsettled Deposits</Text>
                                            <Text theme='h2' color={Colors.text.error}>(${deposit_limit.instant_deposits.toFixed(2)})</Text>
                                        </View>
                                        <View transparent type='row' style={{ padding:4 }}>
                                            <Text theme='h2' style={{ flex:1 }}>Withdrawable Balance</Text>
                                            <Text theme='h1'>{available_for_withdraw.toFixed(2)}</Text>
                                        </View>
                                    </View>
                                    <View float style={{ marginTop:20, padding:10 }}>
                                        <View transparent type='row' style={{ padding:4 }}>
                                            <Text theme='description' style={{ flex:1 }}>Daily Limit</Text>
                                            <Text theme='h2'>${withdraw_limit.ach_daily_limit.toFixed(2)}</Text>
                                        </View>
                                        <View transparent type='row' style={{ padding:4, borderBottomWidth:1, borderColor:Colors.borders.light }}>
                                            <Text theme='description' style={{ flex:1 }}>Rolling 24 hour withdraws</Text>
                                            <Text theme='h2' color={Colors.text.error}>(${withdraw_limit.last_days_ach_withdraws.toFixed(2)})</Text>
                                        </View>
                                        <View transparent type='row' style={{ padding:4 }}>
                                            <Text theme='h2' style={{ flex:1 }}>Limit Available</Text>
                                            <Text theme='h1'>${(limit_available).toFixed(2)}</Text>
                                        </View>
                                    </View>
                                </View>
                            </View>
                        </View>
                    </Pressable>
                )
            default: return <></>
        }
    }


    if(!draft_order){
        return (
            <View style={{ padding:20 }}>
                <ActivityIndicator style={{ alignSelf:'center' }} size='large' color={Colors.text.h1} />
            </View>
        )
    }

    return (
        <View style={{ flex:1 }}>
            <View style={{flex:1}}>
                <FlatList
                    data={sections}
                    key='withdraw_sections'
                    keyExtractor={item => item}
                    renderItem={renderSections}
                />
                {errors.length > 0 ?
                <View float nativeID='errors' style={{ borderBottomRightRadius:0, borderBottomLeftRadius:0 }}>
                    <View transparent style={{ flexDirection:'row', alignItems:'center', padding:15, borderTopRightRadius:8, borderTopLeftRadius:8, backgroundColor:Colors.text.error, borderBottomWidth:1, borderBottomColor:Colors.borders.light  }}>
                        <Icons.AlertIcon color={Colors.text.white} size={24}/>
                        <View transparent style={{ flex:1, marginLeft:10 }}>
                            <Text theme='h1' size={14} color={Colors.text.white}>ERRORS</Text>
                            <Text style={{ marginTop: 3}} theme='description' color={Colors.text.white}>Please fix the following before continuing</Text>
                        </View>
                    </View>
                    <View transparent style={{ padding:10 }}>
                    {errors.map(e => {
                        return (
                            <Text size={14} theme='h2' color={Colors.text.error}>{e}</Text>
                        )
                    })}
                    </View>
                </View>
                :<></>}
            </View>
            <View type='footer' style={{ flexDirection:'row', padding:20 }}>
                <Button
                    style={{ flex:1, marginRight:5, opacity: 1 }}
                    title={'CANCEL'}
                    type='close'
                    onPress={() => onCancel()}
                />
                <Button
                    style={{ flex:3, opacity: errors.length > 0 ? 0.5: 1 }}
                    disabled={errors.length > 0 ? true : false}
                    title={'REVIEW WITHDRAW'}
                    type='success'
                    onPress={() => handleTransact()}
                />
            </View>
            {confirm_ach ?
            <View type='blur' style={{ position:'absolute', top:0, left:0, right:0, bottom:0, flex:1, padding:20 }}>
                <ManualACHAdd
                    account_id={account.account_id}
                    player={player}
                    onAddAccount={(account) => console.log(account)}
                    onUpdateAchDetails={(ach_detail) => {
                        console.log('HERE!!!')
                        let new_account = { ...account }
                        new_account.ach_detail = ach_detail
                        console.log(new_account) 
                        onAccountUpdate(new_account);
                        setConfirmAch(false);
                    }}
                    onCancel={() => setConfirmAch(false)}
                    onFocusPosition={onFocusPosition}
                />
            </View>
            :<></>}
        </View>
    )
}

export default WithdrawCard